import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ElementRef, HostListener, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ArmourFilter, GearHighlightConfig, JewelFilter, JewelHighlightConfig, JewelleryFilter, JewelleryHighlightConfig, WeaponFilter } from '../../../models/filter-models';
import { BaseTypeTier, DisplayType, GearBaseTypeMode, JewelleryType, Rarity } from '../../../models/enums';
import { EquipmentType } from "../../../models/equipment-type.enum";
import { BaseComponent } from '../../../common/base.component';
import { EquipmentTierPickerComponent } from '../../../common/equipment-tier-picker/equipment-tier-picker.component';
import { SelectComponent } from '../../../common/select/select.component';
import { SelectOptionComponent } from '../../../common/select/select.component';
import { Tier } from '../../../models/item-data-models';
import { CtrlKeyService } from '../../../services/ctrl-key.service';
import { NavigationService } from '../../../services/navigation.service';
import { socketGroupKeyDown } from '../../../common/helper';

@Component({
  selector: 'app-my-equipment-highlights',
  imports: [FormsModule, EquipmentTierPickerComponent, SelectComponent, SelectOptionComponent],
  templateUrl: './my-equipment-highlights.component.html',
  styleUrl: './my-equipment-highlights.component.scss'
})
export class MyEquipmentHighlightsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input({ required: true }) equipment!: WeaponFilter | ArmourFilter | JewelleryFilter | JewelFilter;
  @Input() gearOrJewellery: WeaponFilter | ArmourFilter | JewelleryFilter | undefined;
  @Input() gear: WeaponFilter | ArmourFilter | undefined;
  @Input() jewellery: JewelleryFilter | undefined;
  @Input() jewel: JewelFilter | undefined;
  @Input() equipmentType?: string;
  @Input() equipmentIndex?: number;
  @Output() updateFilterEmitter = new EventEmitter<void>();

  BaseTypeTier = BaseTypeTier;
  GearBaseTypeMode = GearBaseTypeMode;
  EquipmentType = EquipmentType;
  Rarity = Rarity;
  DisplayType = DisplayType;
  Tier = Tier;

  toggleAllState: boolean = false;
  socketGroupKeyDown = socketGroupKeyDown;
  protected ctrlKeyService!: CtrlKeyService;
  protected navigationService!: NavigationService;
  protected elementRef!: ElementRef<HTMLElement>;
  protected hoveredElements = new Map<HTMLElement, boolean>();


  constructor() {
    super();
    this.ctrlKeyService = inject(CtrlKeyService);
    this.navigationService = inject(NavigationService);
    this.elementRef = inject(ElementRef);

    // Watch for Ctrl key state changes and update hover class accordingly
    effect(() => {
      if (!this.settings.enableRuleNavigation()) return;

      const isCtrlHeld = this.ctrlKeyService.isCtrlHeld();
      this.hoveredElements.forEach((isHovered, element) => {
        if (isHovered) {
          const titleTextElement = element.querySelector('.filter-title-text') as HTMLElement;
          if (titleTextElement) {
            if (isCtrlHeld) {
              titleTextElement.classList.add('hovered');
            } else {
              titleTextElement.classList.remove('hovered');
            }
          }
        }
      });
    });
  }

  ngOnInit() {
    this.calculateToggleAllState();
  }

  ngOnChanges(changes: SimpleChanges) {
    // No longer needed - rule IDs are computed in template
  }

  calculateToggleAllState() {
    const booleanProperties = Object.keys(this.equipment.highlightConfig).filter(key => {
      if (this.equipment.highlightConfig.equipmentType === 'jewel') {
        if (this.poe2 && JewelHighlightConfig.poe1OnlyProperties.includes(key as keyof JewelHighlightConfig)) return false;
        if (this.poe1 && JewelHighlightConfig.poe2OnlyProperties.includes(key as keyof JewelHighlightConfig)) return false;
      } else if (this.equipment.highlightConfig.equipmentType === 'gear') {
        if (this.poe2 && GearHighlightConfig.poe1OnlyProperties.includes(key as keyof GearHighlightConfig)) return false;
        if (this.poe1 && GearHighlightConfig.poe2OnlyProperties.includes(key as keyof GearHighlightConfig)) return false;
      } else if (this.equipment.highlightConfig.equipmentType === 'jewellery') {
        if (this.poe2 && JewelleryHighlightConfig.poe1OnlyProperties.includes(key as keyof JewelleryHighlightConfig)) return false;
        if (this.poe1 && JewelleryHighlightConfig.poe2OnlyProperties.includes(key as keyof JewelleryHighlightConfig)) return false;
      }
      const value = this.equipment.highlightConfig[key as keyof typeof this.equipment.highlightConfig];
      return typeof value === 'boolean';
    });

    const enabledCount = booleanProperties.filter(key => {
      return this.equipment.highlightConfig[key as keyof typeof this.equipment.highlightConfig] === true;
    }).length;

    this.toggleAllState = enabledCount > booleanProperties.length / 2;
  }

  toggleAll() {
    this.toggleAllState = !this.toggleAllState;
    // Set all boolean properties to the new toggle state
    Object.keys(this.equipment.highlightConfig).forEach(key => {
      if (this.equipment.highlightConfig.equipmentType === 'jewel') {
        if (this.poe2 && JewelHighlightConfig.poe1OnlyProperties.includes(key as keyof JewelHighlightConfig)) return;
        if (this.poe1 && JewelHighlightConfig.poe2OnlyProperties.includes(key as keyof JewelHighlightConfig)) return;
      } else if (this.equipment.highlightConfig.equipmentType === 'gear') {
        if (this.poe2 && GearHighlightConfig.poe1OnlyProperties.includes(key as keyof GearHighlightConfig)) return;
        if (this.poe1 && GearHighlightConfig.poe2OnlyProperties.includes(key as keyof GearHighlightConfig)) return;
      } else if (this.equipment.highlightConfig.equipmentType === 'jewellery') {
        if (this.poe2 && JewelleryHighlightConfig.poe1OnlyProperties.includes(key as keyof JewelleryHighlightConfig)) return;
        if (this.poe1 && JewelleryHighlightConfig.poe2OnlyProperties.includes(key as keyof JewelleryHighlightConfig)) return;
      }

      const value = this.equipment.highlightConfig[key as keyof typeof this.equipment.highlightConfig];
      if (typeof value === 'boolean') {
        (this.equipment.highlightConfig as any)[key] = this.toggleAllState;
      }
    });

    // Some highlights should be disabled based on other settings
    if (this.gearOrJewellery && (this.gearOrJewellery.rarity === Rarity.Magic || this.gearOrJewellery.rarity === Rarity.Rare)) {
      this.gearOrJewellery.highlightConfig.regularNormal = false;
    }

    if (this.equipment && this.equipment.rarity === Rarity.Rare) {
      this.equipment.highlightConfig.regularMagic = false;
    }

    if (this.gearOrJewellery && this.gearOrJewellery.rarity === Rarity.Rare) {
      this.gearOrJewellery.highlightConfig.magic = false;
    }

    if (this.gear && this.gear.baseTypeTier === BaseTypeTier.Basic) {
      this.gear.highlightConfig.lowTierMagic = false;
      this.gear.highlightConfig.lowTierRare = false;
    }

    this.updateFilter();
  }

  toggleRegularNormal() {
    if (!this.gearOrJewellery) return;
    this.gearOrJewellery.highlightConfig.regularNormal = !this.gearOrJewellery.highlightConfig.regularNormal;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleRegularMagic() {
    this.equipment.highlightConfig.regularMagic = !this.equipment.highlightConfig.regularMagic;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleRegularRare() {
    this.equipment.highlightConfig.regularRare = !this.equipment.highlightConfig.regularRare;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleHighlightRare() {
    if (!this.gearOrJewellery) return;
    this.gearOrJewellery.highlightConfig.rare = !this.gearOrJewellery.highlightConfig.rare;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleHighlightMagic() {
    if (!this.gearOrJewellery) return;
    this.gearOrJewellery.highlightConfig.magic = !this.gearOrJewellery.highlightConfig.magic;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleHighlightLowTierMagic() {
    if (!this.gear) return;
    this.gear.highlightConfig.lowTierMagic = !this.gear.highlightConfig.lowTierMagic;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleHighlightLowTierRare() {
    if (!this.gear) return;
    this.gear.highlightConfig.lowTierRare = !this.gear.highlightConfig.lowTierRare;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleExcellentILevel() {
    this.equipment.highlightConfig.excellentILevel = !this.equipment.highlightConfig.excellentILevel;
    if (!this.equipment.highlightConfig.excellentILevel) {
      this.equipment.highlightConfig.goodILevel = false;
    }
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleGoodILevel() {
    this.equipment.highlightConfig.goodILevel = !this.equipment.highlightConfig.goodILevel;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleExcellentExceptional() {
    if (!this.gear) return;
    this.gear.highlightConfig.excellentExceptional = !this.gear.highlightConfig.excellentExceptional;
    if (!this.gear.highlightConfig.excellentExceptional) {
      this.gear.highlightConfig.goodExceptional = false;
    }
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleGoodExceptional() {
    if (!this.gear) return;
    this.gear.highlightConfig.goodExceptional = !this.gear.highlightConfig.goodExceptional;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleExcellentCraftingBase() {
    if (!this.gear) return;
    this.gear.highlightConfig.excellentCraftingBase = !this.gear.highlightConfig.excellentCraftingBase;
    if (!this.gear.highlightConfig.excellentCraftingBase) {
      this.gear.highlightConfig.goodCraftingBase = false;
    }
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleGoodCraftingBase() {
    if (!this.gear) return;
    this.gear.highlightConfig.goodCraftingBase = !this.gear.highlightConfig.goodCraftingBase;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleExcellentMemoryStrands() {
    if (this.gear) {
      this.gear.highlightConfig.excellentMemoryStrands = !this.gear.highlightConfig.excellentMemoryStrands;
      if (!this.gear.highlightConfig.excellentMemoryStrands) {
        this.gear.highlightConfig.goodMemoryStrands = false;
      }
    } else if (this.jewellery) {
      this.jewellery.highlightConfig.excellentMemoryStrands = !this.jewellery.highlightConfig.excellentMemoryStrands;
      if (!this.jewellery.highlightConfig.excellentMemoryStrands) {
        this.jewellery.highlightConfig.goodMemoryStrands = false;
      }
    } else {
      return;
    }
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleGoodMemoryStrands() {
    if (this.gear) {
      this.gear.highlightConfig.goodMemoryStrands = !this.gear.highlightConfig.goodMemoryStrands;
    } else if (this.jewellery) {
      this.jewellery.highlightConfig.goodMemoryStrands = !this.jewellery.highlightConfig.goodMemoryStrands;
    } else {
      return;
    }
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleSixLink() {
    if (!this.gear) return;
    this.gear.highlightConfig.sixLink = !this.gear.highlightConfig.sixLink;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleFiveLink() {
    if (!this.gear) return;
    this.gear.highlightConfig.fiveLink = !this.gear.highlightConfig.fiveLink;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  toggleColouredSockets() {
    if (!this.gear) return;
    this.gear.highlightConfig.colouredSockets = !this.gear.highlightConfig.colouredSockets;
    this.calculateToggleAllState();
    this.updateFilter();
  }

  getExampleGearName() {
    if ('weaponType' in this.equipment) {
      return this.equipment.weaponType[0];
    } else if ('armourType' in this.equipment) {
      return this.equipment.armourType[0];
    } else if ('jewelleryBaseTypes' in this.equipment) {
      if (this.equipment.jewelleryBaseTypes[0] === 'All') {
        if (this.equipment.jewelleryType === JewelleryType.Rings) return 'Gold Ring';
        else if (this.equipment.jewelleryType === JewelleryType.Amulets) return 'Gold Amulet';
        else if (this.equipment.jewelleryType === JewelleryType.Belts) return 'Heavy Belt';
        else if (this.equipment.jewelleryType === JewelleryType.Talismans) return 'Horned Talisman';
      }
      return this.equipment.jewelleryBaseTypes[0];
    } else if ('jewelType' in this.equipment) {
      return this.equipment.jewelType[0];
    }
    return null;
  }

  isTierDisabled(tierNumber: number): boolean {
    if (!this.gear) return false;

    if (!this.gear.unidentifiedTier) {
      return false;
    }
    return tierNumber < this.gear.unidentifiedTier;
  }

  getLowerTierName(): string | null {
    if (!this.gear) return null;

    switch (this.gear.baseTypeTier) {
      case BaseTypeTier.Master:
        return 'Elite';
      case BaseTypeTier.Elite:
        return 'Expert';
      case BaseTypeTier.Expert:
        return 'Advanced';
      case BaseTypeTier.Advanced:
        return 'Basic';
      case BaseTypeTier.Basic:
      default:
        return null;
    }
  }

  updateFilter() {
    this.updateFilterEmitter.emit();
  }

  onHighlightMouseEnter(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;

    const target = event.currentTarget as HTMLElement;
    const ruleId = target.getAttribute('data-filter-rule-id');
    if (!ruleId) return;

    this.hoveredElements.set(target, true);
    if (this.ctrlKeyService.isCtrlHeld()) {
      const titleTextElement = target.querySelector('.filter-title-text') as HTMLElement;
      if (titleTextElement) {
        titleTextElement.classList.add('hovered');
      }
    }
  }

  onHighlightMouseLeave(event: MouseEvent) {
    if (!this.settings.enableRuleNavigation()) return;

    const target = event.currentTarget as HTMLElement;
    this.hoveredElements.set(target, false);
    const titleTextElement = target.querySelector('.filter-title-text') as HTMLElement;
    if (titleTextElement) {
      titleTextElement.classList.remove('hovered');
    }
  }

  onHighlightClick(event: MouseEvent, usePrefixMatch: boolean = false) {
    if (!this.settings.enableRuleNavigation()) return;
    if (!(event.ctrlKey || event.metaKey)) return;

    const target = event.currentTarget as HTMLElement;
    const ruleId = target.getAttribute('data-filter-rule-id');
    if (!ruleId) return;

    event.preventDefault();
    event.stopPropagation();
    // Use exact match for highlight rules (they have equipment type/index appended)
    this.navigationService.navigateToFilterOutput(ruleId, usePrefixMatch);
  }
}

